import { Helmet } from 'react-helmet-async';
import ogImage from '@/assets/og-image.png';

const BASE_URL = 'https://harborcoparent.com';
// In production builds Vite rewrites the import to a hashed absolute CDN URL
// (so social scrapers fetch from CDN). In dev the import is a relative path
// like `/src/assets/og-image.png`, so we prepend BASE_URL to keep og:image absolute.
const DEFAULT_OG_IMAGE = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`;

export interface SEOProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export function SEO({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd,
  noindex = false,
}: SEOProps) {
  const canonicalUrl = `${BASE_URL}${path}`;
  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name='description' content={description} />
      <link rel='canonical' href={canonicalUrl} />
      {noindex && <meta name='robots' content='noindex,nofollow' />}

      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta property='og:url' content={canonicalUrl} />
      <meta property='og:image' content={ogImage} />
      <meta property='og:type' content={ogType} />
      <meta property='og:site_name' content='Harbor' />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={ogImage} />

      {jsonLdArray.map((data, i) => (
        <script
          key={i}
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data).replace(/</g, '\\u003c'),
          }}
        />
      ))}
    </Helmet>
  );
}
